import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SeoMeta Component
 * Used to set dynamic meta tags for different pages
 * 
 * Props:
 * - title: Page title (required)
 * - description: Meta description
 * - image: OG image URL
 * - url: Canonical URL
 * - type: og:type (default: website)
 * - keywords: SEO keywords
 * - author: Page author
 */
const SeoMeta = ({
  title,
  description,
  image = 'https://brightangel.lk/og-image.png',
  url = 'https://brightangel.lk',
  type = 'website',
  keywords = '',
  author = 'Bright Angel Study Abroad',
  children,
}) => {
  // Ensure title includes brand name
  const fullTitle = title.includes('Bright Angel') 
    ? title 
    : `${title} | Bright Angel Study Abroad`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={author} />
      <meta name="copyright" content="2024 Bright Angel Study Abroad" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />

      {/* Canonical URL */}
      <link rel="canonical" href={url} />

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Bright Angel Study Abroad" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:creator" content="@brightangelstudyabroad" />

      {/* Additional Meta Tags for Mobile */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Additional */}
      {children}
    </Helmet>
  );
};

export default SeoMeta;
